import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Input, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { RobotOutlined, CheckCircleOutlined } from '@ant-design/icons';
import AiAgreementIndicator from '../AiAgreementIndicator';
import ReasonModal from './ReasonModal';
import EditQuestionModal from './EditQuestionModal';
import { useApiRequest } from '../../hooks/useApiRequest';
import { openServerUrl } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import {
    DRAFT_STATE_LABELS,
    approveBlockReason,
    approveBreakGlassAvailable,
    breakGlassAuditText,
    breakGlassReasonValid,
    buildApproveBody,
    canApproveDraft,
    canEditDraft,
    canRejectDraft,
    clauseAckRequired,
    clauseFieldFor,
    describeApproveError,
    fieldLabel,
} from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

const quoteStyle = { margin: '4px 0', padding: '6px 10px', borderLeft: '3px solid var(--color-text-muted)', background: 'var(--color-surface-sunken)', whiteSpace: 'pre-wrap' };

/** The quote where it sits in the original text, match highlighted, so the reviewer can judge what surrounds it. */
const QuoteInContext = ({ context }) => {
    if (!context) {
        return (
            <Text type="secondary" data-testid="quote-context-unavailable" style={{ fontSize: 12 }}>
                The quote could not be located in the original text, so no surrounding context is shown.
            </Text>
        );
    }
    return (
        <div data-testid="draft-quote-context" style={{ ...quoteStyle, fontSize: 13 }}>
            <Text type="secondary">In context: </Text>
            {context.before}<mark data-testid="quote-match">{context.match}</mark>{context.after}
        </div>
    );
};

const Provenance = ({ provenance, quoteInContext, clauseInSource }) => {
    const p = provenance || {};
    if (!p.sourceRef && !p.quote) {
        return <Text type="secondary" data-testid="no-provenance">No source provenance was recorded for this draft.</Text>;
    }
    const openSource = () => {
        if (!openServerUrl(p.url)) message.warning('The source link could not be opened. It must be an https address and pop-ups must be allowed.');
    };
    return (
        <div data-testid="draft-provenance">
            <Text strong>Source</Text>{' '}
            <Text>{p.sourceLabel || p.sourceRef}</Text>{' '}
            <Tag>{p.sourceKind === 'url' ? 'Fetched URL' : 'Pasted or uploaded text'}</Tag>
            {p.url && <Button size="small" onClick={openSource}>Open source</Button>}
            {p.sourceSha256 && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>Source fingerprint (SHA-256): {String(p.sourceSha256).slice(0, 16)}</Text></div>
            )}
            <blockquote style={quoteStyle} data-testid="draft-quote">{p.quote}</blockquote>
            <QuoteInContext context={quoteInContext} />
            <Space wrap>
                {/* Neutral colour on purpose: this is a text match, not a verdict on the question or the source. */}
                {p.quoteFoundInSource
                    ? <Tag color="blue" data-testid="badge-quote-found">Quote found in the source text</Tag>
                    : <Tag color="red" data-testid="badge-quote-not-found">Quote not found in the source text</Tag>}
                {p.clauseMatchesTarget
                    ? <Tag color="green" data-testid="badge-clause-matches">Clause matches the target clause</Tag>
                    : <Tag color="orange" data-testid="badge-clause-differs">Clause differs from the target clause</Tag>}
                {clauseInSource
                    ? <Tag color="green" data-testid="badge-clause-in-source">Clause appears in the source text</Tag>
                    : <Tag color="orange" data-testid="badge-clause-not-in-source">Clause not found in the source text</Tag>}
            </Space>
            <div>
                <Text type="secondary" style={{ fontSize: 12 }} data-testid="quote-found-caveat">
                    A matching quote does not show that the question is supported by the source, or that the source is trustworthy. Read the quote and judge both.
                </Text>
            </div>
        </div>
    );
};

const diffColumns = [
    { title: 'Field', key: 'field', render: (_, r) => fieldLabel(r.field) },
    { title: 'AI original', key: 'ai', render: (_, r) => String(r.ai ?? '-') },
    { title: 'Current', key: 'current', render: (_, r) => String(r.current ?? '-') },
];

/**
 * One AI-drafted question for human review (Commandment IV): provenance (quote in context) and
 * AI-vs-current diff on the card, why no agreement score exists, and the three human actions.
 * An AI draft is never live until a person approves it here AND the version is published.
 * Approve is gated by the server's readiness block (thin-row fields, clause acknowledgement) and binds
 * to the content hash the reviewer saw; the API enforces the same rules.
 */
const AiDraftCard = ({ draft, standardId, onChanged }) => {
    const { request } = useApiRequest();
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [ackClause, setAckClause] = useState(false);
    const [unsafeField, setUnsafeField] = useState(null);
    const [breakGlass, setBreakGlass] = useState(false);
    const [breakGlassReason, setBreakGlassReason] = useState('');

    // An acknowledgement applies to the content that was on screen when it was ticked.
    useEffect(() => { setAckClause(false); }, [draft.contentHash]);

    const ai = draft.ai || {};
    const review = draft.review || {};
    const readiness = draft.readiness || {};
    const approval = draft.approval || {};
    const blockReason = approveBlockReason(draft);
    const needsAck = clauseAckRequired(draft);
    const clauseField = clauseFieldFor(standardId || draft.standardId);
    const editRow = {
        id: draft.questionId,
        questionNo: draft.questionNo,
        question: draft.question,
        responseType: draft.responseType,
        priority: draft.priority,
        domain: draft.domain,
        severity: draft.severity,
        mandatory: draft.mandatory,
        evidenceRequired: draft.evidenceRequired,
        expectedRespondent: draft.expectedRespondent,
        ...(clauseField ? { [clauseField]: draft.clauseRef } : {}),
    };

    const approve = async () => {
        setBusy('approve');
        setError(null);
        try {
            await request('POST', `/admin/question-bank/ai-drafts/${draft.id}/approve`, {
                body: buildApproveBody(draft, { acknowledgeClause: ackClause, breakGlass, breakGlassReason }),
            });
            message.success(`Approved ${draft.questionNo}. It is not live until you publish the version.`);
            await onChanged();
        } catch (err) {
            const view = describeApproveError(err);
            setError(view.message);
            setUnsafeField(view.unsafeField || null);
            if (view.reload) await onChanged();
        } finally {
            setBusy(null);
        }
    };

    const reject = async (reason) => {
        await request('POST', `/admin/question-bank/ai-drafts/${draft.id}/reject`, { body: { reason } });
        message.success(`Rejected ${draft.questionNo}.`);
        setRejectOpen(false);
        await onChanged();
    };

    const saved = async (data) => {
        setEditOpen(false);
        setUnsafeField(null);
        setError(null);
        if (data?.approvalReset) message.warning(`Saved. Your approval of ${draft.questionNo} was withdrawn - review and approve it again.`);
        else message.success(`Saved ${draft.questionNo}.`);
        await onChanged();
    };

    const approved = draft.state === 'APPROVED' || draft.state === 'PUBLISHED';
    const offerBreakGlass = approveBreakGlassAvailable(draft);
    const useBreakGlass = offerBreakGlass && breakGlass;
    const approveDisabled = busy !== null || !!blockReason || (needsAck && !ackClause) || (useBreakGlass && !breakGlassReasonValid(breakGlassReason));

    return (
        <Card
            size="small"
            data-testid="ai-draft-card"
            title={<span>{draft.questionNo || 'Draft'} <Tag color={approved ? 'green' : draft.state === 'REJECTED' ? 'red' : 'orange'} icon={approved ? <CheckCircleOutlined /> : <RobotOutlined />}>{draft.state === 'AI_DRAFT' ? 'AI drafted - review' : DRAFT_STATE_LABELS[draft.state] || draft.state}</Tag></span>}
            style={{ marginBottom: 12 }}
        >
            <p style={{ marginTop: 0 }} data-testid="draft-question">{draft.question}</p>
            <Space wrap style={{ marginBottom: 8 }}>
                {draft.clauseRef && <Tag>Clause: {draft.clauseRef}</Tag>}
                {draft.priority && <Tag>Priority: {draft.priority}</Tag>}
                {draft.responseType && <Tag>Answer: {draft.responseType}</Tag>}
                {draft.domain && <Tag>Domain: {draft.domain}</Tag>}
                {draft.severity && <Tag>Severity: {draft.severity}</Tag>}
                {draft.mandatory && <Tag>Mandatory: {draft.mandatory}</Tag>}
                {draft.evidenceRequired && <Tag>Evidence required: {draft.evidenceRequired}</Tag>}
                {draft.expectedRespondent && <Tag>Respondent: {draft.expectedRespondent}</Tag>}
                {draft.editedByHuman && <Tag color="blue" data-testid="badge-edited">Edited by a human</Tag>}
            </Space>
            {draft.rationale && <p><Text strong>Why the AI proposed it:</Text> {draft.rationale}</p>}

            <Provenance
                provenance={draft.provenance}
                quoteInContext={draft.quoteInContext}
                clauseInSource={readiness.clauseInSource ?? draft.provenance?.clauseInSource}
            />

            {draft.editedByHuman && Array.isArray(draft.diff) && draft.diff.length > 0 && (
                <div style={{ marginTop: 8 }} data-testid="draft-diff">
                    <Text strong>AI original vs current</Text>
                    <Table size="small" pagination={false} columns={diffColumns} dataSource={draft.diff} rowKey={(r) => r.field} />
                </div>
            )}

            <div style={{ marginTop: 8 }}>
                <AiAgreementIndicator
                    confidence={null}
                    unavailableReason={ai.confidenceUnavailableReason}
                    scopeLabel={ai.confidenceMeaning}
                    approved={approved}
                    testId="ai-draft-confidence-tag"
                />
            </div>

            {(review.approvedBy || review.rejectedBy || review.publishedBy) && (
                <p style={{ marginTop: 8, marginBottom: 0 }} data-testid="draft-review-trail">
                    {review.approvedBy && <span>Approved by {review.approvedBy} on {formatDdMmYyyy(review.approvedAt)}. </span>}
                    {review.rejectedBy && <span>Rejected by {review.rejectedBy} on {formatDdMmYyyy(review.rejectedAt)}. </span>}
                    {review.publishedBy && <span>Published by {review.publishedBy} on {formatDdMmYyyy(review.publishedAt)}.</span>}
                </p>
            )}

            {(approval.clauseNotInSourceAcknowledged || approval.breakGlass || approval.publishBreakGlass) && (
                <div style={{ marginTop: 8 }} data-testid="draft-approval-flags">
                    {approval.clauseNotInSourceAcknowledged && <Tag color="orange">Approved with the clause not found in the source (acknowledged)</Tag>}
                    {approval.breakGlass && <Tag color="red">{breakGlassAuditText('Approved under break-glass', approval.breakGlass)}</Tag>}
                    {approval.publishBreakGlass && <Tag color="red">{breakGlassAuditText('Published under break-glass', approval.publishBreakGlass)}</Tag>}
                </div>
            )}
            {approval.contentChangedSinceApproval === true && (
                <Alert
                    type="warning"
                    style={{ marginTop: 8 }}
                    data-testid="content-changed-since-approval"
                    message="This draft was edited after it was approved."
                    description="The earlier approval no longer applies. Review the current content and approve it again."
                />
            )}

            {canApproveDraft(draft) && blockReason && (
                <Alert type="warning" style={{ marginTop: 8 }} data-testid="approve-blocked-reason" message={blockReason} />
            )}
            {canApproveDraft(draft) && needsAck && (
                <div style={{ marginTop: 8 }}>
                    <Checkbox checked={ackClause} onChange={(e) => setAckClause(e.target.checked)}>
                        <span data-testid="clause-ack-label">
                            The clause reference "{draft.clauseRef}" does not appear in the cited source text. I have checked it and confirm it is correct.
                        </span>
                    </Checkbox>
                </div>
            )}
            {canApproveDraft(draft) && offerBreakGlass && (
                <div style={{ marginTop: 8 }} data-testid="break-glass-approve">
                    <Checkbox checked={breakGlass} onChange={(e) => setBreakGlass(e.target.checked)}>
                        Approve under break-glass. The server allows this because you are the only platform admin; it is recorded in the audit log.
                    </Checkbox>
                    {breakGlass && (
                        <Input.TextArea
                            aria-label="Break-glass reason"
                            rows={2}
                            placeholder="Why can a second admin not approve? (at least 15 characters)"
                            value={breakGlassReason}
                            onChange={(e) => setBreakGlassReason(e.target.value)}
                        />
                    )}
                </div>
            )}

            {error && <Alert type="error" message={error} data-testid="draft-action-error" style={{ marginTop: 8 }} />}

            {(canApproveDraft(draft) || canRejectDraft(draft) || canEditDraft(draft)) && (
                <Space style={{ marginTop: 12 }}>
                    {canApproveDraft(draft) && (
                        <Tooltip title="Records your approval. The question still goes live only when the version is published.">
                            <Button onClick={approve} loading={busy === 'approve'} disabled={approveDisabled}>Approve</Button>
                        </Tooltip>
                    )}
                    {canEditDraft(draft) && <Button onClick={() => setEditOpen(true)}>Edit</Button>}
                    {canRejectDraft(draft) && <Button danger onClick={() => setRejectOpen(true)}>Reject</Button>}
                </Space>
            )}

            <ReasonModal
                open={rejectOpen}
                title={`Reject ${draft.questionNo}`}
                intro="The draft is discarded and will not be published. Your reason is recorded in the audit log."
                okText="Reject draft"
                onSubmit={reject}
                onClose={() => setRejectOpen(false)}
            />
            <EditQuestionModal
                open={editOpen}
                mode="edit"
                row={editRow}
                standardId={standardId || draft.standardId}
                approvalWillReset={draft.state === 'APPROVED'}
                missingFields={(readiness.missingFields || []).map((m) => m.field)}
                unsafeField={unsafeField}
                onStale={onChanged}
                onClose={() => setEditOpen(false)}
                onSaved={saved}
            />
        </Card>
    );
};

export default AiDraftCard;
